import { useState, useCallback } from "react";
import {
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from "../../../../hooks/queries/useRoleQueries";
import { toastService } from "../../../../providers";

export const useRoleHandlers = ({ modalMode, selectedRole, closeModal }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { mutate: createRole, isPending: isCreating } = useCreateRoleMutation();

  const { mutate: updateRole, isPending: isUpdating } = useUpdateRoleMutation();

  const { mutate: deleteRole, isPending: isDeleting } = useDeleteRoleMutation();

  const handleSubmit = useCallback(
    async (values) => {
      const closeModalAndReset = () => {
        closeModal();
      };

      if (modalMode === "create") {
        createRole(values, {
          onSuccess: () => {
            toastService.success("Role created successfully!");
            closeModalAndReset();
          },
          onError: (_err) => {
            toastService.error("Failed to create role");
          },
        });
      } else if (modalMode === "edit" && selectedRole) {
        updateRole(
          {
            id: selectedRole.id,
            roleData: values,
          },
          {
            onSuccess: () => {
              toastService.success("Role updated successfully!");
              closeModalAndReset();
            },
            onError: (_err) => {
              toastService.error("Failed to update role");
            },
          },
        );
      }
    },
    [modalMode, selectedRole, createRole, updateRole, closeModal],
  );

  const handleDeleteRole = useCallback((role) => {
    if (!role?.id) return;
    setPendingDelete(role);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!pendingDelete?.id) return;
    deleteRole(pendingDelete.id, {
      onSuccess: () => {
        toastService.success("Role deleted");
      },
      onError: (_err) => {
        toastService.error("Failed to delete role");
      },
    });
    setConfirmOpen(false);
    setPendingDelete(null);
  }, [pendingDelete, deleteRole]);

  const handleCancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setPendingDelete(null);
  }, []);

  return {
    handleSubmit,
    handleDeleteRole,
    confirmOpen,
    pendingDelete,
    handleConfirmDelete,
    handleCancelDelete,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

export default useRoleHandlers;
